document.addEventListener('DOMContentLoaded', () => {
  if (!window.gsap) return

  const { gsap } = window

  const section = document.querySelector('[data-home-formulation-section]')
  if (!section) return

  const buildRevealLines = (html) => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return Array.from(tmp.querySelectorAll('p'))
      .map((p) => p.textContent.trim())
      .filter(Boolean)
      .map((line) => `<span class="block overflow-hidden"><span class="block" data-formulation-line>${line}</span></span>`)
      .join('')
  }

  const swapTokens = new WeakMap()

  const swapTitle = (element, nextHtml, fast) => {
    if (!element || !nextHtml) return

    const currentLines = element.querySelectorAll('[data-formulation-line]')
    const nextBuilt = buildRevealLines(nextHtml)
    const token = (swapTokens.get(element) || 0) + 1
    swapTokens.set(element, token)

    const outDuration = fast ? 0.35 : 0.45
    const outStagger = fast ? 0.05 : 0.06
    const inDuration = fast ? 0.65 : 0.7
    const inStagger = fast ? 0.08 : 0.1

    const animateOut = currentLines.length
      ? gsap.to(currentLines, {
          yPercent: -120,
          opacity: 0,
          duration: outDuration,
          ease: 'power2.in',
          stagger: outStagger,
        })
      : gsap.to(element, { opacity: 0, duration: fast ? 0.2 : 0.3, ease: 'sine.out' })

    animateOut.then(() => {
      if (swapTokens.get(element) !== token) return

      element.innerHTML = nextBuilt
      const newLines = element.querySelectorAll('[data-formulation-line]')
      gsap.set(newLines, { yPercent: 120, opacity: 0 })
      gsap.to(newLines, {
        yPercent: 0,
        opacity: 1,
        duration: inDuration,
        ease: 'power3.out',
        stagger: inStagger,
      })
    })
  }

  // Desktop ingredient swap
  const ingredients = Array.from(section.querySelectorAll('[data-ingredient]'))
  const mainImg = section.querySelector('[data-formulation-main-img]')
  const isMainImg = mainImg && mainImg.tagName === 'IMG'
  const heading = section.querySelector('[data-formulation-heading]')
  const titleStore = Array.from(section.querySelectorAll('[data-formulation-title-item]'))

  if (ingredients.length) {
    let currentSrc = isMainImg ? mainImg.getAttribute('src') : null
    let pendingSrc = null
    let activeOverlay = null
    let swapId = 0
    let activeIndex = '0'

    if (isMainImg) {
      ingredients.forEach((item) => {
        const src = item.getAttribute('data-ingredient-image')
        if (src) {
          const preload = new Image()
          preload.src = src
        }
      })
    }

    const commitMainImage = (src) => {
      if (!src || !isMainImg) return
      mainImg.src = src
      mainImg.removeAttribute('srcset')
      mainImg.removeAttribute('sizes')
      currentSrc = src
      pendingSrc = null
    }

    const showMainImage = (src) => {
      if (!src || !isMainImg) return
      if (src === pendingSrc) return
      if (src === currentSrc && !activeOverlay) return

      const id = ++swapId
      pendingSrc = src

      if (activeOverlay) {
        gsap.killTweensOf(activeOverlay)
        if (activeOverlay.complete && activeOverlay.naturalWidth > 0) {
          commitMainImage(activeOverlay.currentSrc || activeOverlay.getAttribute('src'))
        }
        activeOverlay.remove()
        activeOverlay = null
      }

      if (src === currentSrc) {
        pendingSrc = null
        return
      }

      const overlay = mainImg.cloneNode(false)
      overlay.removeAttribute('data-formulation-main-img')
      overlay.removeAttribute('loading')
      overlay.removeAttribute('srcset')
      overlay.removeAttribute('sizes')
      overlay.style.position = 'absolute'
      overlay.style.top = '0'
      overlay.style.left = '0'
      overlay.style.width = '100%'
      overlay.style.height = '100%'
      overlay.style.opacity = '0'
      activeOverlay = overlay

      const startFade = () => {
        if (id !== swapId) {
          overlay.remove()
          if (activeOverlay === overlay) activeOverlay = null
          return
        }

        gsap.fromTo(
          overlay,
          { opacity: 0, scale: 1.04 },
          {
            opacity: 1,
            scale: 1,
            duration: 0.55,
            ease: 'power2.out',
            onComplete: () => {
              if (id !== swapId) return
              commitMainImage(src)
              requestAnimationFrame(() => {
                overlay.remove()
                if (activeOverlay === overlay) activeOverlay = null
              })
            },
          },
        )
      }

      overlay.addEventListener('load', startFade, { once: true })
      overlay.src = src
      mainImg.parentElement.appendChild(overlay)

      if (overlay.complete && overlay.naturalWidth > 0) startFade()
    }

    const setActiveIngredient = (active) => {
      const index = active.getAttribute('data-ingredient-index')
      if (index === activeIndex) return
      activeIndex = index

      ingredients.forEach((item) => {
        item.classList.toggle('opacity-30', item !== active)
      })

      const titleItem = titleStore.find((item) => item.getAttribute('data-formulation-index') === index)
      if (heading && titleItem) {
        swapTitle(heading, titleItem.innerHTML)
      }

      showMainImage(active.getAttribute('data-ingredient-image'))
    }

    ingredients.forEach((item) => {
      item.addEventListener('focus', () => setActiveIngredient(item))
      item.addEventListener('click', () => setActiveIngredient(item))
    })

    if (window.ScrollTrigger) {
      const progressBars = ingredients.map((item) => item.querySelector('[data-ingredient-progress]'))
      const listWrapper = ingredients[0].parentElement
      const count = ingredients.length

      const indexForProgress = (progress) => Math.min(Math.floor(Math.max(0, Math.min(progress, 1)) * count), count - 1)

      const pinTarget = section.querySelector('[data-formulation-desktop]')
      const listViewport = section.querySelector('[data-formulation-list-viewport]')

      if (pinTarget && listViewport) {
        const SCROLL_PER_INGREDIENT = 0.8
        const MIN_CLEARANCE = 0.15
        const MIN_GAP_RATIO = 0.45
        const PEEK_MIN = 0.22
        const PEEK_MAX = 0.8

        const naturalHeight = () => {
          const { paddingTop, minHeight } = listWrapper.style
          listWrapper.style.paddingTop = '0px'
          listWrapper.style.minHeight = '0px'
          const height = listWrapper.offsetHeight
          listWrapper.style.paddingTop = paddingTop
          listWrapper.style.minHeight = minHeight
          return height
        }

        const itemBounds = () => {
          const base = ingredients[0].offsetTop
          return ingredients.map((item) => ({ top: item.offsetTop - base, height: item.offsetHeight }))
        }

        // Keep the fold off a sliver of an ingredient
        const snapToItem = (padding, free, items, floor) => {
          const fold = free - padding
          const cut = items.find((item) => fold > item.top && fold < item.top + item.height)
          if (!cut) return padding

          const visible = (fold - cut.top) / cut.height
          if (visible >= PEEK_MIN && visible <= PEEK_MAX) return padding

          if (visible > PEEK_MAX) {
            const whole = free - cut.top - cut.height
            return whole >= floor ? whole : free - cut.top - cut.height * PEEK_MAX
          }

          const peek = free - cut.top - cut.height * PEEK_MIN
          return peek >= floor ? peek : free - cut.top
        }

        // Mandatory spacing under the title, only needed when the list cannot fit
        const restingOffset = () => {
          listWrapper.style.paddingTop = ''

          const designed = parseFloat(getComputedStyle(listWrapper).paddingTop) || 0
          const free = listViewport.clientHeight
          const content = naturalHeight()
          const items = itemBounds()
          const floor = designed * MIN_GAP_RATIO
          const ceiling = Math.max(0, free - items[0].height * PEEK_MIN)

          if (free >= content) return 0

          return Math.min(Math.max(snapToItem(designed, free, items, floor), floor), ceiling)
        }

        const setupPin = () => gsap.matchMedia().add('(min-width: 1200px)', () => {
          let maxShift = 0
          let hold = 0
          let trigger = null

          const measure = () => {
            const nextPadding = `${restingOffset()}px`
            if (listWrapper.style.paddingTop !== nextPadding) {
              listWrapper.style.paddingTop = nextPadding
            }

            maxShift = Math.max(0, listWrapper.offsetHeight - listViewport.clientHeight)

            const clearance = ingredients[0].offsetHeight * MIN_CLEARANCE
            const headroom = Math.max(0, ingredients[0].offsetTop - clearance)
            const spare = maxShift - headroom
            const needed = spare > 0 ? (maxShift - headroom * count) / spare : 0

            hold = Math.max(0, needed) / count
          }

          measure()

          const timeline = gsap.timeline({
            paused: true,
            defaults: { ease: 'none' },
            onUpdate: () => setActiveIngredient(ingredients[indexForProgress(timeline.progress())]),
          })

          timeline.to(
            listWrapper,
            {
              y: () => -maxShift,
              duration: count,
              ease: (progress) => (progress <= hold ? 0 : (progress - hold) / (1 - hold)),
            },
            0,
          )

          progressBars.forEach((bar, i) => {
            if (!bar) return
            timeline.fromTo(bar, { scaleX: 0 }, { scaleX: 1, duration: 1 }, i)
          })

          trigger = window.ScrollTrigger.create({
            trigger: pinTarget,
            start: 'top top',
            end: () => `+=${count * window.innerHeight * SCROLL_PER_INGREDIENT}`,
            pin: pinTarget,
            pinSpacing: true,
            scrub: 0.35,
            refreshPriority: 1,
            animation: timeline,
          })

          let resizeTimer = null
          const onResize = () => {
            clearTimeout(resizeTimer)
            resizeTimer = setTimeout(() => {
              if (trigger?.isActive) return
              measure()
              window.ScrollTrigger.refresh()
            }, 150)
          }

          window.addEventListener('resize', onResize)

          return () => {
            window.removeEventListener('resize', onResize)
            clearTimeout(resizeTimer)
            trigger.kill()
            timeline.kill()
            listWrapper.style.paddingTop = ''
            gsap.set(listWrapper, { clearProps: 'y' })
            progressBars.forEach((bar) => bar && gsap.set(bar, { clearProps: 'transform' }))
          }
        })

        if (window.whenScrollReady) window.whenScrollReady(setupPin)
        else setupPin()
      }
    }
  }

  // Mobile slider
  const slides = Array.from(section.querySelectorAll('[data-mslide]'))
  const dots = Array.from(section.querySelectorAll('[data-formulation-dot]'))
  const mobileHeading = section.querySelector('[data-formulation-mobile-heading]')
  const mobileLabel = section.querySelector('[data-formulation-mobile-display-label]')
  const mobileBodies = Array.from(section.querySelectorAll('[data-formulation-mobile-body-panel]'))
  const mobileStore = Array.from(section.querySelectorAll('[data-formulation-mobile-item]'))

  let mobileSwapReady = false

  if (slides.length) {
    const total = slides.length
    let activeIndex = 0

    const SIDE_X = 44.267
    const FAR_X = 62
    const SIDE_SCALE = 0.35
    const FAR_SCALE = 0.3
    const vwToPx = () => window.innerWidth / 100

    let activeBodyPanel = mobileBodies.find((panel) => !panel.classList.contains('invisible')) || mobileBodies[0]

    const swapBody = (index) => {
      const next = mobileBodies.find((panel) => panel.getAttribute('data-formulation-index') === String(index))
      if (!next || next === activeBodyPanel) return

      const current = activeBodyPanel
      activeBodyPanel = next

      gsap.killTweensOf([current, next])
      gsap
        .timeline()
        .to(current, { autoAlpha: 0, duration: 0.25, ease: 'sine.out' })
        .add(() => {
          current.classList.add('invisible')
          next.classList.remove('invisible')
        })
        .fromTo(next, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.35, ease: 'sine.in' })
    }

    const updateMobileContent = (index) => {
      const item = mobileStore.find((entry) => entry.getAttribute('data-formulation-index') === String(index))
      if (!item) return

      const title = item.querySelector('[data-formulation-mobile-title]')
      const label = item.querySelector('[data-formulation-mobile-label]')

      if (mobileHeading && title && mobileSwapReady) swapTitle(mobileHeading, title.innerHTML, true)
      if (mobileLabel && label && mobileSwapReady) {
        gsap.to(mobileLabel, {
          autoAlpha: 0,
          duration: 0.25,
          ease: 'sine.out',
          onComplete: () => {
            mobileLabel.textContent = label.textContent
            gsap.to(mobileLabel, { autoAlpha: 1, duration: 0.35, ease: 'sine.in' })
          },
        })
      }
      if (mobileSwapReady) swapBody(index)
    }

    const relativePosition = (index) => {
      let rel = index - activeIndex
      if (rel > total / 2) rel -= total
      if (rel < -total / 2) rel += total
      return rel
    }

    const slotFor = (rel) => {
      if (rel === 0) return { x: 0, scale: 1, opacity: 1, z: 3 }
      if (rel === 1) return { x: SIDE_X, scale: SIDE_SCALE, opacity: 0.3, z: 2 }
      if (rel === -1) return { x: -SIDE_X, scale: SIDE_SCALE, opacity: 0.3, z: 2 }
      return { x: rel > 0 ? FAR_X : -FAR_X, scale: FAR_SCALE, opacity: 0, z: 1 }
    }

    const layout = (animate) => {
      const unit = vwToPx()

      slides.forEach((slide, i) => {
        const slot = slotFor(relativePosition(i))
        const props = {
          xPercent: -50,
          x: slot.x * unit,
          scale: slot.scale,
          autoAlpha: slot.opacity,
          zIndex: slot.z,
          overwrite: 'auto',
        }

        if (animate) {
          gsap.to(slide, { ...props, duration: 1, ease: 'power3.out' })
        } else {
          gsap.set(slide, props)
        }
      })
    }

    const updateDots = () => {
      dots.forEach((dot, i) => {
        dot.classList.toggle('bg-black-200', i === activeIndex)
        dot.classList.toggle('bg-black-12', i !== activeIndex)
      })
    }

    const goToSlide = (index) => {
      const next = ((index % total) + total) % total
      if (next === activeIndex) return
      activeIndex = next
      updateMobileContent(activeIndex)
      updateDots()
      layout(true)
    }

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => goToSlide(i))
    })

    slides.forEach((slide, i) => {
      slide.addEventListener('click', () => goToSlide(i))
    })

    const slider = section.querySelector('[data-formulation-mslider]')
    if (slider) {
      const DIRECTION_LOCK = 10
      let startX = 0
      let startY = 0
      let isHorizontal = null

      slider.addEventListener(
        'touchstart',
        (event) => {
          startX = event.touches[0].clientX
          startY = event.touches[0].clientY
          isHorizontal = null
        },
        { passive: true },
      )

      slider.addEventListener(
        'touchmove',
        (event) => {
          const deltaX = event.touches[0].clientX - startX
          const deltaY = event.touches[0].clientY - startY

          if (isHorizontal === null) {
            if (Math.abs(deltaX) < DIRECTION_LOCK && Math.abs(deltaY) < DIRECTION_LOCK) return
            isHorizontal = Math.abs(deltaX) > Math.abs(deltaY)
          }

          if (isHorizontal) event.preventDefault()
        },
        { passive: false },
      )

      slider.addEventListener('touchend', (event) => {
        if (!isHorizontal) return
        const deltaX = startX - event.changedTouches[0].clientX
        if (deltaX > 50) goToSlide(activeIndex + 1)
        else if (deltaX < -50) goToSlide(activeIndex - 1)
      })
    }

    let resizeTimer = null
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => layout(false), 150)
    })

    updateMobileContent(activeIndex)
    updateDots()
    layout(false)
  }

  const headingLines = [...section.querySelectorAll('[data-formulation-line]')].filter((el) => el.offsetParent)
  const revealItems = [...section.querySelectorAll('[data-formulation-reveal]')].filter((item) => item.getClientRects().length)

  const reveal = () => {
    gsap.to(headingLines, {
      yPercent: 0,
      opacity: 1,
      duration: 0.9,
      ease: 'power3.out',
      stagger: 0.12,
      delay: 0.15,
    })

    gsap.to(revealItems, {
      y: 0,
      autoAlpha: 1,
      duration: 1.1,
      ease: 'power3.out',
      stagger: 0.12,
    })
  }

  const buildEntrance = () => {
    if (headingLines.length) gsap.set(headingLines, { yPercent: 120, opacity: 0 })
    gsap.set(revealItems, { y: 40, autoAlpha: 0 })
  }

  // On load animation
  buildEntrance()
  mobileSwapReady = true

  if (window.ScrollTrigger) {
    window.ScrollTrigger.create({
      trigger: section,
      start: 'top 75%',
      onEnter: reveal,
    })
    window.ScrollTrigger.create({
      trigger: section,
      start: 'top bottom',
      onLeaveBack: buildEntrance,
    })
  } else {
    reveal()
  }
})
